<template>
  <Layout>
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="page-title-box d-flex align-items-center justify-content-between">
            <h4 class="mb-0">Transferências</h4>
            <div>
              <button
                class="btn btn-success"
                @click="openWithdrawModal"
                :disabled="!canRequestWithdraw"
                :title="getWithdrawButtonTooltip"
              >
                <i class="mdi mdi-arrow-up mr-2"></i>
                Solicitar Saque
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Avisos -->
      <div class="row" v-if="!recipient">
        <div class="col-12">
          <div class="alert alert-warning">
            <i class="mdi mdi-alert mr-2"></i>
            <strong>Atenção:</strong> Nenhum recebedor cadastrado. Configure um recebedor antes de solicitar saques.
          </div>
        </div>
      </div>

      <div class="row" v-if="recipient && availableForTransfer < 10">
        <div class="col-12">
          <div class="alert alert-info">
            <i class="mdi mdi-information mr-2"></i>
            <strong>Informação:</strong> Saldo disponível insuficiente para solicitar saque. O valor mínimo é R$ 10,00 (+ taxa de transferência de {{ formatMoney(transferFee) }}).
          </div>
        </div>
      </div>

      <loading
        :active.sync="loading"
        color="#556ee6"
        loader="dots"
        :can-cancel="false"
        :is-full-page="false">
      </loading>

      <!-- Informações da Conta -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">Informações da Conta</h5>
              <div class="row" v-if="recipient">
                <div class="col-md-3">
                  <div class="mb-0">
                    <p class="text-muted mb-1">Nome do Recebedor</p>
                    <h6 class="mb-0">{{ recipient.name }}</h6>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="mb-0">
                    <p class="text-muted mb-1">Agência</p>
                    <h6 class="mb-0">{{ recipient.branch_number }}<span v-if="recipient.branch_check_digit">-{{ recipient.branch_check_digit }}</span></h6>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="mb-0">
                    <p class="text-muted mb-1">Código do Banco</p>
                    <h6 class="mb-0">{{ recipient.bank_code }} - {{ getBankName(recipient.bank_code) }}</h6>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="mb-0">
                    <p class="text-muted mb-1">Conta</p>
                    <h6 class="mb-0">{{ recipient.account_number }}-{{ recipient.account_check_digit }}</h6>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="mb-0">
                    <p class="text-muted mb-1">Tipo de Conta</p>
                    <h6 class="mb-0">{{ recipient.account_type === 'checking' ? 'Conta Corrente' : 'Conta Poupança' }}</h6>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted">
                <p>Nenhuma conta bancária cadastrada</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Visão Geral do Saldo -->
      <div class="row">
        <div class="col-12">
          <h5 class="mb-3">Visão Geral do Saldo</h5>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Saldo Atual</p>
                  <h4 class="mb-0">{{ formatMoney(balance.total_amount) }}</h4>
                  <small class="text-muted">Última atualização: {{ lastUpdate }}</small>
                </div>
                <div class="avatar-sm bg-primary-soft align-self-center">
                  <i class="mdi mdi-wallet text-primary font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">A Receber</p>
                  <h4 class="mb-0">{{ formatMoney(balance.waiting_funds_amount) }}</h4>
                  <small class="text-muted">&nbsp;</small>
                </div>
                <div class="avatar-sm bg-warning-soft align-self-center">
                  <i class="mdi mdi-clock-outline text-warning font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Disponível p/ Saque</p>
                  <h4 class="mb-0">{{ formatMoney(availableForTransfer) }}</h4>
                  <small class="text-muted">Taxa de transferência: {{ formatMoney(transferFee) }}</small>
                </div>
                <div class="avatar-sm bg-success-soft align-self-center">
                  <i class="mdi mdi-cash-multiple text-success font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-3">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Solicitações Pendentes</p>
                  <h4 class="mb-0">{{ formatMoney(pendingAmount) }}</h4>
                  <small class="text-warning">{{ pendingCount }} solicitações em processamento</small>
                </div>
                <div class="avatar-sm bg-warning-soft align-self-center">
                  <i class="mdi mdi-timer-sand text-warning font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Histórico de Transações -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-4">
                <h5 class="card-title mb-0">Histórico de Transações</h5>
                <div class="d-flex align-items-center">
                  <input
                    type="date"
                    v-model="filters.start_date"
                    class="form-control form-control-sm mr-2"
                    style="width: 150px;"
                  >
                  <span class="mx-2">até</span>
                  <input
                    type="date"
                    v-model="filters.end_date"
                    class="form-control form-control-sm mr-2"
                    style="width: 150px;"
                  >
                  <select v-model="filters.status" class="form-control form-control-sm mr-2" style="width: 180px;">
                    <option value="">Todos os status</option>
                    <option value="pending">Pendente</option>
                    <option value="processing">Em Processamento</option>
                    <option value="transferred">Pago</option>
                    <option value="failed">Falhou</option>
                  </select>
                  <button class="btn btn-outline-primary btn-sm" @click="exportTransactions">
                    <i class="mdi mdi-download mr-1"></i>
                    Exportar
                  </button>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Tipo de Solicitação</th>
                      <th>ID Transferência</th>
                      <th>Data</th>
                      <th>Horário</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th class="text-center">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="transfer in transfers" :key="transfer.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="avatar-xs mr-3 bg-primary-soft d-flex align-items-center justify-content-center">
                            <i class="mdi mdi-bank-transfer text-primary font-size-16"></i>
                          </div>
                          Saque
                        </div>
                      </td>
                      <td>{{ transfer.pagarme_transfer_id || '-' }}</td>
                      <td>{{ formatDate(transfer.created_at) }}</td>
                      <td>{{ formatTime(transfer.created_at) }}</td>
                      <td><strong>{{ formatMoney(transfer.amount) }}</strong></td>
                      <td>
                        <span class="badge badge-pill" :class="'badge-' + getStatusColor(transfer.status)">
                          {{ getStatusLabel(transfer.status) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <button
                          class="btn btn-sm btn-outline-primary"
                          @click="openTransferDetails(transfer)"
                          title="Ver detalhes"
                        >
                          <i class="mdi mdi-eye"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="transfers.length === 0 && !loading">
                      <td colspan="7" class="text-center text-muted py-4">
                        Nenhuma transferência encontrada
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Paginação -->
              <div v-if="totalPages > 1" class="row mt-4">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center">
                    <p class="mb-0 text-muted">Mostrando {{ (currentPage - 1) * perPage + 1 }}-{{ Math.min(currentPage * perPage, totalTransfers) }} de {{ totalTransfers }} resultados</p>
                    <ul class="pagination mb-0">
                      <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Anterior</a>
                      </li>
                      <li
                        v-for="page in displayPages"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === currentPage }"
                      >
                        <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                      </li>
                      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Próximo</a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes da Transferência -->
    <b-modal
      v-model="detailsModal"
      title="Detalhes da Transferência"
      size="lg"
      ok-only
      ok-title="Fechar"
      ok-variant="secondary"
    >
      <div v-if="selectedTransfer">
        <!-- Informações Gerais -->
        <h6 class="text-uppercase text-muted mb-3">Informações Gerais</h6>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Status</label>
            <div>
              <span class="badge badge-pill" :class="'badge-' + getStatusColor(selectedTransfer.status)">
                {{ getStatusLabel(selectedTransfer.status) }}
              </span>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Tipo de Transferência</label>
            <div class="font-weight-medium text-uppercase">{{ pagarmeDetails.type || 'TED' }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Data da Solicitação</label>
            <div class="font-weight-medium">{{ formatDateTime(selectedTransfer.created_at) }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Última Atualização</label>
            <div class="font-weight-medium">{{ formatDateTime(selectedTransfer.updated_at) }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Data da Transferência</label>
            <div class="font-weight-medium">{{ pagarmeDetails.funding_date ? formatDate(pagarmeDetails.funding_date) : '-' }}</div>
          </div>
        </div>

        <hr>

        <!-- IDs -->
        <h6 class="text-uppercase text-muted mb-3">Identificação</h6>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">ID do Recebedor</label>
            <div class="font-weight-medium small">{{ pagarmeDetails.recipient_id || '-' }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">ID da Transferência</label>
            <div class="font-weight-medium">{{ selectedTransfer.pagarme_transfer_id || '-' }}</div>
          </div>
        </div>

        <hr>

        <!-- Valores -->
        <h6 class="text-uppercase text-muted mb-3">Valores</h6>
        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="text-muted mb-1">Valor Solicitado</label>
            <div class="font-weight-medium text-success">+ {{ formatMoney(pagarmeDetails.requested_amount || selectedTransfer.amount) }}</div>
          </div>
          <div class="col-md-4 mb-3">
            <label class="text-muted mb-1">Taxa de Transferência</label>
            <div class="font-weight-medium text-danger">- {{ formatMoney(pagarmeDetails.fee || transferFee) }}</div>
          </div>
          <div class="col-md-4 mb-3">
            <label class="text-muted mb-1">Total Transferido</label>
            <div class="font-weight-bold text-success">+ {{ formatMoney(pagarmeDetails.total_transferred || (selectedTransfer.amount - transferFee)) }}</div>
          </div>
        </div>

        <template v-if="pagarmeDetails.source_bank">
          <hr>

          <!-- Detalhes do Pagador (Source - Stone) -->
          <h6 class="text-uppercase text-muted mb-3">Detalhes do Pagador</h6>
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Nome/Razão Social do Pagador</label>
              <div class="font-weight-medium">{{ pagarmeDetails.source_bank.holder_name || '-' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">CPF/CNPJ</label>
              <div class="font-weight-medium">{{ pagarmeDetails.source_bank.holder_document || '-' }}</div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="text-muted mb-1">Banco</label>
              <div class="font-weight-medium">{{ pagarmeDetails.source_bank.bank }} - {{ pagarmeDetails.source_bank.bank_name }}</div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="text-muted mb-1">Agência</label>
              <div class="font-weight-medium">{{ pagarmeDetails.source_bank.branch_number || '-' }}</div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="text-muted mb-1">Conta</label>
              <div class="font-weight-medium">Indisponível</div>
            </div>
          </div>

          <hr>

          <!-- Detalhes do Favorecido (Target - Conta bancária) -->
          <h6 class="text-uppercase text-muted mb-3">Detalhes do Favorecido</h6>
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Nome/Razão Social do Pagador</label>
              <div class="font-weight-medium">{{ pagarmeDetails.target_bank.holder_name || '-' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">CPF/CNPJ</label>
              <div class="font-weight-medium">{{ formatDocument(pagarmeDetails.target_bank.holder_document) }}</div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="text-muted mb-1">Banco</label>
              <div class="font-weight-medium">{{ formatBankWithName(pagarmeDetails.target_bank.bank) }}</div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="text-muted mb-1">Agência</label>
              <div class="font-weight-medium">{{ pagarmeDetails.target_bank.branch_number || '-' }}</div>
            </div>
            <div class="col-md-4 mb-3">
              <label class="text-muted mb-1">Conta</label>
              <div class="font-weight-medium">{{ formatAccount(pagarmeDetails.target_bank) }}</div>
            </div>
          </div>
        </template>

        <div v-else class="text-center text-muted py-3">
          <i class="mdi mdi-information-outline font-size-24 d-block mb-2"></i>
          Detalhes do Pagar.me não disponíveis para esta transferência.
        </div>
      </div>
    </b-modal>

    <!-- Modal de Saque -->
    <b-modal
      v-model="withdrawModal"
      title="Solicitar Saque"
      @ok="handleWithdrawOk"
      ok-title="Confirmar Solicitação"
      ok-variant="success"
      cancel-title="Cancelar"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
    >
      <div class="alert alert-info mb-3">
        <div class="d-flex justify-content-between">
          <span><strong>Saldo Disponível:</strong> {{ formatMoney(balance.available_amount) }}</span>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <span>Taxa de transferência:</span>
          <span>- {{ formatMoney(transferFee) }}</span>
        </div>
        <hr class="my-2">
        <div class="d-flex justify-content-between">
          <strong>Disponível para saque:</strong>
          <strong>{{ formatMoney(availableForTransfer) }}</strong>
        </div>
      </div>

      <div class="form-group">
        <label>Valor do Saque <span class="text-danger">*</span></label>
        <div class="input-group">
          <div class="input-group-prepend">
            <span class="input-group-text">R$</span>
          </div>
          <input
            type="number"
            v-model.number="withdrawForm.amount"
            class="form-control"
            min="10"
            :max="availableForTransfer"
            step="0.01"
            required
            @keypress.enter="handleWithdrawOk"
          >
        </div>
        <small class="form-text text-muted">
          Valor mínimo: R$ 10,00 | Máximo: {{ formatMoney(availableForTransfer) }}
        </small>
        <small v-if="withdrawForm.amount && withdrawForm.amount < 10" class="form-text text-danger">
          Valor abaixo do mínimo permitido
        </small>
        <small v-if="withdrawForm.amount && withdrawForm.amount > availableForTransfer" class="form-text text-danger">
          Valor maior que o disponível para saque
        </small>
      </div>

      <div class="alert alert-warning mb-0">
        <i class="mdi mdi-information mr-2"></i>
        <strong>Atenção:</strong> A transferência será processada e o valor será depositado na conta bancária cadastrada.
        O prazo para crédito é de 1 dia útil.
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { getBalance, getTransfers, getTransfer, createTransfer, getRecipientByEvent } from '../recipient/services'

export default {
  components: {
    Layout,
    Loading
  },
  data () {
    return {
      loading: false,
      eventId: null,
      recipient: null,
      balance: {
        total_amount: 0,
        available_amount: 0,
        waiting_funds_amount: 0,
        currency: 'BRL'
      },
      transfers: [],
      filters: {
        start_date: '',
        end_date: '',
        status: ''
      },
      transferFee: 0,
      detailsModal: false,
      selectedTransfer: null,
      withdrawModal: false,
      withdrawForm: {
        amount: 0
      },
      currentPage: 1,
      perPage: 10,
      totalTransfers: 0
    }
  },
  computed: {
    lastUpdate () {
      return this.$moment().format('DD/MM/YYYY HH:mm')
    },
    pendingAmount () {
      return this.transfers
        .filter(t => t.status === 'pending' || t.status === 'processing')
        .reduce((sum, t) => sum + parseFloat(t.amount), 0)
    },
    pendingCount () {
      return this.transfers.filter(t => t.status === 'pending' || t.status === 'processing').length
    },
    totalPages () {
      return Math.ceil(this.totalTransfers / this.perPage)
    },
    displayPages () {
      const pages = []
      const maxPages = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxPages / 2))
      let end = Math.min(this.totalPages, start + maxPages - 1)

      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
    pagarmeDetails () {
      return (this.selectedTransfer && this.selectedTransfer.pagarme_details) || {}
    },
    availableForTransfer () {
      return this.balance.available_for_transfer ?? Math.max(0, this.balance.available_amount - this.transferFee)
    },
    canRequestWithdraw () {
      return this.recipient && this.availableForTransfer >= 10
    },
    getWithdrawButtonTooltip () {
      if (!this.recipient) {
        return 'Configure um recebedor antes de solicitar saques'
      }
      if (this.availableForTransfer < 10) {
        return 'Saldo insuficiente para saque (mínimo R$ 10,00 + taxa de R$ ' + this.transferFee.toFixed(2).replace('.', ',') + ')'
      }
      return 'Solicitar saque do saldo disponível'
    }
  },
  mounted () {
    // Sistema single-tenant: usa 1 como padrão
    this.eventId = 1
    this.loadRecipient()
    this.loadBalance()
    this.loadTransfers()
  },
  methods: {
    async loadRecipient () {
      try {
        const response = await getRecipientByEvent(this.eventId)
        this.recipient = response.data
        console.log('Recebedor carregado:', this.recipient)
      } catch (error) {
        console.error('Erro ao carregar recebedor:', error)
        if (error.response?.status === 404) {
          this.$toast.warning('Nenhum recebedor cadastrado. Configure um recebedor na página de Configuração.')
        }
      }
    },
    async loadBalance () {
      this.loading = true
      try {
        const response = await getBalance(this.eventId)
        this.balance = response.data
        this.transferFee = parseFloat(response.data.transfer_fee) || 0
        console.log('Saldo carregado:', this.balance)
      } catch (error) {
        console.error('Erro ao carregar saldo:', error)
        this.$toast.error('Erro ao carregar saldo')
      } finally {
        this.loading = false
      }
    },
    async loadTransfers () {
      this.loading = true
      try {
        const response = await getTransfers(this.eventId, {
          ...this.filters,
          page: this.currentPage,
          per_page: this.perPage
        })
        this.transfers = response.data.data || response.data
        this.totalTransfers = response.data.total || this.transfers.length
      } catch (error) {
        this.$toast.error('Erro ao carregar transferências')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    openWithdrawModal () {
      console.log('Abrindo modal de saque', {
        recipient: this.recipient,
        balance: this.balance
      })

      if (!this.recipient) {
        this.$toast.error('Configure um recebedor antes de solicitar saques')
        return
      }

      if (this.availableForTransfer < 10) {
        this.$toast.error('Saldo insuficiente para solicitar saque')
        return
      }

      // Definir valor padrão como o saldo disponível (já descontada a taxa)
      this.withdrawForm.amount = parseFloat(this.availableForTransfer.toFixed(2))
      this.withdrawModal = true
    },
    async handleWithdrawOk (bvModalEvent) {
      // Prevenir o fechamento automático do modal
      bvModalEvent.preventDefault()

      // Chamar o método de solicitação de saque
      await this.requestWithdraw()
    },
    async requestWithdraw () {
      // Validações
      if (!this.withdrawForm.amount || this.withdrawForm.amount <= 0) {
        this.$toast.error('Por favor, informe um valor válido')
        return
      }

      if (this.withdrawForm.amount < 10) {
        this.$toast.error('Valor mínimo para saque é R$ 10,00')
        return
      }

      if (this.withdrawForm.amount > this.availableForTransfer) {
        this.$toast.error('Valor maior que o disponível para saque')
        return
      }

      this.loading = true
      try {
        const response = await createTransfer(this.eventId, this.withdrawForm)
        console.log('Saque criado:', response)

        this.$toast.success('Solicitação de saque criada com sucesso!')
        this.withdrawModal = false

        // Recarregar dados
        await this.loadBalance()
        await this.loadTransfers()
      } catch (error) {
        console.error('Erro ao solicitar saque:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao solicitar saque'
        this.$toast.error(errorMessage)
      } finally {
        this.loading = false
      }
    },
    changePage (page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadTransfers()
      }
    },
    exportTransactions () {
      this.$toast.info('Exportação em desenvolvimento')
    },
    async openTransferDetails (transfer) {
      this.selectedTransfer = transfer
      this.detailsModal = true
      try {
        const response = await getTransfer(this.eventId, transfer.id)
        this.selectedTransfer = response.data
      } catch (error) {
        console.error('Erro ao carregar detalhes da transferência:', error)
      }
    },
    formatDateTime (date) {
      return date ? this.$moment(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    formatDocument (doc) {
      if (!doc) return '-'
      const clean = doc.replace(/\D/g, '')
      if (clean.length === 11) {
        return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      }
      if (clean.length === 14) {
        return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
      }
      return doc
    },
    formatBankWithName (bankCode) {
      if (!bankCode) return '-'
      const name = this.getBankName(bankCode)
      return name ? `${bankCode} - ${name}` : bankCode
    },
    formatAccount (bankData) {
      if (!bankData || !bankData.account_number) return '-'
      const digit = bankData.account_check_digit ? `-${bankData.account_check_digit}` : ''
      return `${bankData.account_number}${digit}`
    },
    formatMoney (value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    },
    formatDate (date) {
      return this.$moment(date).format('DD/MM/YYYY')
    },
    formatTime (date) {
      return this.$moment(date).format('HH:mm')
    },
    getStatusLabel (status) {
      const labels = {
        pending: 'Pendente',
        pending_transfer: 'Pendente',
        processing: 'Em Processamento',
        transferred: 'Transferido',
        paid: 'Pago',
        failed: 'Falhou',
        canceled: 'Cancelado'
      }
      return labels[status] || status
    },
    getStatusColor (status) {
      const colors = {
        pending: 'warning',
        pending_transfer: 'warning',
        processing: 'info',
        transferred: 'success',
        paid: 'success',
        failed: 'danger',
        canceled: 'danger'
      }
      return colors[status] || 'secondary'
    },
    getBankName (code) {
      const banks = {
        '001': 'Banco do Brasil',
        '033': 'Santander',
        '104': 'Caixa Econômica',
        '237': 'Bradesco',
        '341': 'Itaú Unibanco',
        '077': 'Banco Inter',
        '260': 'Nubank',
        '290': 'PagSeguro',
        '323': 'Mercado Pago'
      }
      return banks[code] || ''
    }
  }
}
</script>

<style scoped>
.bg-soft {
  background-color: rgba(85, 110, 230, 0.1);
}

.bg-soft-primary {
  background-color: rgba(85, 110, 230, 0.1) !important;
}

.bg-soft-warning {
  background-color: rgba(244, 106, 106, 0.1) !important;
}

.bg-soft-success {
  background-color: rgba(52, 195, 143, 0.1) !important;
}

.bg-primary-soft {
  background-color: rgba(85, 110, 230, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.bg-warning-soft {
  background-color: rgba(242, 184, 39, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.bg-success-soft {
  background-color: rgba(52, 195, 143, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.avatar-xs {
  height: 2rem;
  width: 2rem;
  min-width: 2rem;
  border-radius: 0.5rem;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
  min-width: 3rem;
  border-radius: 0.5rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.font-size-16 {
  font-size: 16px !important;
}
</style>
